/**
 * Entity Configuration System
 * Maps entity names to UI configuration, features, and branding
 */

export type EntityName = 'fdaia' | 'al3viewer' | 'mvc' | 'services' | 'community';

export interface EntityFeature {
  id: string;
  label: string;
  enabled: boolean;
}

export interface EntityConfig {
  id: EntityName;
  title: string;
  shortName: string;
  description: string;
  primaryColor: 'cyan' | 'purple' | 'blue' | 'green' | 'orange';
  features: EntityFeature[];
  layoutMode: 'sidebar-canvas-panel' | 'split-view' | 'gallery-view';
  hasAI: boolean;
}

export const ENTITY_CONFIGS: Record<EntityName, EntityConfig> = {
  fdaia: {
    id: 'fdaia',
    title: 'CK SAaS Footwear Design Studio',
    shortName: 'FDS',
    description: 'AI-powered footwear design and variant generation',
    primaryColor: 'cyan',
    features: [
      { id: 'colorSwatches', label: 'Color Swatches', enabled: true },
      { id: 'materialPicker', label: 'Material Selection', enabled: true },
      { id: 'styleOptions', label: 'Style Options', enabled: true },
      { id: 'aiAssistant', label: 'AI Design Assistant', enabled: true },
    ],
    layoutMode: 'sidebar-canvas-panel',
    hasAI: true,
  },
  al3viewer: {
    id: 'al3viewer',
    title: 'AL3Viewer - Asset Lineage License Ledger',
    shortName: 'AL3V',
    description: 'Blockchain-based IP protection and licensing',
    primaryColor: 'purple',
    features: [
      { id: 'userJourney', label: 'User Journey', enabled: true },
      { id: 'assetViewer', label: 'Asset Preview', enabled: true },
      { id: 'lineageTrace', label: 'Lineage Trace', enabled: true },
      { id: 'paymentFlow', label: 'License Payment', enabled: true },
    ],
    layoutMode: 'split-view',
    hasAI: false,
  },
  mvc: {
    id: 'mvc',
    title: 'M-VC - My Virtual Closet',
    shortName: 'M-VC',
    description: 'Social commerce platform for footwear collections',
    primaryColor: 'cyan',
    features: [
      { id: 'styleCategories', label: 'Style Categories', enabled: true },
      { id: 'virtualTryOn', label: 'Virtual Try-On', enabled: true },
      { id: 'aiStylist', label: 'AI Styling Assistant', enabled: true },
      { id: 'socialSharing', label: 'Social Sharing', enabled: true },
    ],
    layoutMode: 'gallery-view',
    hasAI: true,
  },
  services: {
    id: 'services',
    title: 'CK SAaS Services',
    shortName: 'Services',
    description: 'Commercial operations and business services',
    primaryColor: 'blue',
    features: [
      { id: 'dashboard', label: 'Service Dashboard', enabled: true },
      { id: 'orderManagement', label: 'Order Management', enabled: true },
      { id: 'analytics', label: 'Analytics', enabled: true },
    ],
    layoutMode: 'sidebar-canvas-panel',
    hasAI: false,
  },
  community: {
    id: 'community',
    title: 'Community Gateway',
    shortName: 'Comm-GW',
    description: 'Community engagement and content distribution',
    primaryColor: 'green',
    features: [
      { id: 'feed', label: 'Community Feed', enabled: true },
      { id: 'discussions', label: 'Discussions', enabled: true },
      { id: 'events', label: 'Events', enabled: true },
    ],
    layoutMode: 'gallery-view',
    hasAI: false,
  },
};

/**
 * Get entity configuration from environment or route
 */
export function getEntityConfig(entityName: string): EntityConfig {
  const config = ENTITY_CONFIGS[entityName as EntityName];

  if (!config) {
    throw new Error(`Unknown entity: ${entityName}`);
  }

  return config;
}

/**
 * Check if feature is enabled for entity
 */
export function isFeatureEnabled(entityName: EntityName, featureId: string): boolean {
  const config = ENTITY_CONFIGS[entityName];
  const feature = config.features.find(f => f.id === featureId);
  return feature?.enabled ?? false;
}